import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { CameraParams } from './types';

// Constants
const CENTER = new THREE.Vector3(0, 0.75, 0);
const BASE_DISTANCE = 1.6;
const AZIMUTH_RADIUS = 2.4;
// Keep elevation ring the same radius as azimuth ring (visual consistency).
const ELEVATION_RADIUS = AZIMUTH_RADIUS;

function createPlaceholderTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    if (ctx) {
        ctx.fillStyle = '#3a3a4a';
        ctx.fillRect(0, 0, 256, 256);
        ctx.fillStyle = '#ffcc99';
        ctx.beginPath();
        ctx.arc(128, 128, 80, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#333';
        ctx.beginPath();
        ctx.arc(100, 110, 10, 0, Math.PI * 2);
        ctx.arc(156, 110, 10, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(128, 130, 35, 0.2, Math.PI - 0.2);
        ctx.stroke();
    }
    return new THREE.CanvasTexture(canvas);
}

const AZIMUTH_STEPS = [0, 45, 90, 135, 180, 225, 270, 315];
// Elevation is stored as "仰角" degrees where 90° = eye level.
// Discrete steps correspond to pitch (-30/0/30/60) => elevation (60/90/120/150).
const ELEVATION_STEPS = [60, 90, 120, 150];
const DISTANCE_STEPS = [0.6, 1.0, 1.4];

function snapToNearest(value: number, steps: number[]) {
    return steps.reduce((prev, curr) =>
        Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
    );
}

interface CameraControl3DProps {
    value: CameraParams;
    onChange: (value: Partial<CameraParams>) => void;
    className?: string;
    /** Increment to reset the viewport camera (orbit view) back to defaults */
    resetViewNonce?: number;
}

export const CameraControl3D = ({ value, onChange, className, resetViewNonce }: CameraControl3DProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mountRef = useRef<HTMLDivElement>(null);

    // NOTE: event handlers are registered once in an effect; use refs to avoid stale closures.
    const valueRef = useRef<CameraParams>(value);
    const onChangeRef = useRef<(value: Partial<CameraParams>) => void>(onChange);
    useEffect(() => {
        valueRef.current = value;
        onChangeRef.current = onChange;
    }, [value, onChange]);

    // Refs for scene objects
    const sceneObjects = useRef<{
        cameraGroup: THREE.Group;
        azimuthHandle: THREE.Mesh;
        elevationHandle: THREE.Mesh;
        distanceHandle: THREE.Mesh;
        distanceLineGeo: THREE.BufferGeometry;
        targetPlane: THREE.Mesh;
        planeMaterial: THREE.MeshBasicMaterial;
        scene: THREE.Scene;
        camera: THREE.PerspectiveCamera;
        renderer: THREE.WebGLRenderer;
        azimuthRing: THREE.Mesh;
        elevationArcLimited: THREE.Mesh;
        elevationArcFull: THREE.Mesh;
    } | null>(null);

    // State refs to avoid closure staleness
    const state = useRef({
        azimuth: value.azimuth,
        elevation: value.elevation,
        distance: value.distance,
        isDragging: false,
        dragTarget: null as THREE.Object3D | null,
        dragStartMouse: new THREE.Vector2(),
        dragStartDistance: 1.0,
        // View (orbit) controls: rotate the 3D viewport camera without affecting tool params
        isOrbiting: false,
        orbitStartMouse: new THREE.Vector2(),
        orbitStartViewAzimuth: 0,
        orbitStartViewElevation: 0,
        viewAzimuth: 45, // degrees
        viewElevation: 25, // degrees
        viewRadius: 7.0,
    });

    const continuous = Boolean((value as any)?.continuous);

    // Update internal state when props change (if not dragging)
    useEffect(() => {
        if (!state.current.isDragging) {
            state.current.azimuth = value.azimuth;
            state.current.elevation = value.elevation;
            state.current.distance = value.distance;
            updatePositions();
        }
    }, [value.azimuth, value.elevation, value.distance]);

    // Toggle arc visualization for elevation control based on continuous mode
    useEffect(() => {
        try {
            const objs = sceneObjects.current;
            if (!objs) return;
            objs.elevationArcLimited.visible = !continuous;
            objs.elevationArcFull.visible = continuous;
        } catch {}
    }, [continuous]);

    const updatePositions = () => {
        if (!sceneObjects.current) return;
        const { cameraGroup, azimuthHandle, elevationHandle, distanceHandle, distanceLineGeo } = sceneObjects.current;
        const { azimuth, elevation, distance } = state.current;

        const distVal = BASE_DISTANCE * distance;
        const azRad = THREE.MathUtils.degToRad(azimuth);
        // Convert "仰角" (0–360, 90=eye level) -> pitch degrees (elevation - 90), then to radians.
        const pitchDeg = ((elevation - 90 + 540) % 360) - 180; // normalize to [-180,180]
        const elRad = THREE.MathUtils.degToRad(pitchDeg);

        const camX = distVal * Math.sin(azRad) * Math.cos(elRad);
        const camY = distVal * Math.sin(elRad) + CENTER.y;
        const camZ = distVal * Math.cos(azRad) * Math.cos(elRad);

        cameraGroup.position.set(camX, camY, camZ);
        cameraGroup.lookAt(CENTER);

        // Keep both azimuth + elevation controls centered on the same orbit center (CENTER).
        azimuthHandle.position.set(
            CENTER.x + AZIMUTH_RADIUS * Math.sin(azRad),
            CENTER.y,
            CENTER.z + AZIMUTH_RADIUS * Math.cos(azRad)
        );
        elevationHandle.position.set(
            CENTER.x,
            CENTER.y + ELEVATION_RADIUS * Math.sin(elRad),
            CENTER.z + ELEVATION_RADIUS * Math.cos(elRad)
        );

        const orangeDist = distVal - 0.5;
        distanceHandle.position.set(
            orangeDist * Math.sin(azRad) * Math.cos(elRad),
            orangeDist * Math.sin(elRad) + CENTER.y,
            orangeDist * Math.cos(azRad) * Math.cos(elRad)
        );
        distanceLineGeo.setFromPoints([cameraGroup.position.clone(), CENTER.clone()]);
    };

    const updateViewCamera = () => {
        if (!sceneObjects.current) return;
        const { camera } = sceneObjects.current;
        const { viewAzimuth, viewElevation, viewRadius } = state.current;
        const az = THREE.MathUtils.degToRad(viewAzimuth);
        const el = THREE.MathUtils.degToRad(viewElevation);
        const r = Math.max(2.2, Math.min(20, viewRadius));
        const x = CENTER.x + r * Math.sin(az) * Math.cos(el);
        const y = CENTER.y + r * Math.sin(el);
        const z = CENTER.z + r * Math.cos(az) * Math.cos(el);
        camera.position.set(x, y, z);
        camera.lookAt(CENTER);
    };

    // Reset orbit view camera when requested by parent (e.g., Reset button)
    useEffect(() => {
        try {
            // Stop interactions
            state.current.isOrbiting = false;
            state.current.isDragging = false;
            state.current.dragTarget = null;
            // Reset to defaults
            state.current.viewAzimuth = 45;
            state.current.viewElevation = 25;
            state.current.viewRadius = 7.0;
            updateViewCamera();
        } catch {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetViewNonce]);

    // Setup Effect
    useEffect(() => {
        if (!mountRef.current) return;
        const container = mountRef.current;
        const width = container.clientWidth;
        const height = container.clientHeight;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x1a1a1a);

        const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
        camera.position.set(4.5, 3, 4.5);
        camera.lookAt(0, 0.75, 0);

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        container.appendChild(renderer.domElement);

        // Lighting
        scene.add(new THREE.AmbientLight(0xffffff, 0.6));
        const dirLight = new THREE.DirectionalLight(0xffffff, 0.6);
        dirLight.position.set(5, 10, 5);
        scene.add(dirLight);
        scene.add(new THREE.GridHelper(8, 16, 0x333333, 0x222222));

        // Initial Objects
        const planeMaterial = new THREE.MeshBasicMaterial({ map: createPlaceholderTexture(), side: THREE.DoubleSide });
        const targetPlane = new THREE.Mesh(new THREE.PlaneGeometry(1.2, 1.2), planeMaterial);
        targetPlane.position.copy(CENTER);
        scene.add(targetPlane);

        // Camera Model
        const cameraGroup = new THREE.Group();
        const bodyMat = new THREE.MeshStandardMaterial({ color: 0x6699cc, metalness: 0.5, roughness: 0.3 });
        const body = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.22, 0.38), bodyMat);
        cameraGroup.add(body);
        const lens = new THREE.Mesh(
            new THREE.CylinderGeometry(0.09, 0.11, 0.18, 16),
            new THREE.MeshStandardMaterial({ color: 0x6699cc, metalness: 0.5, roughness: 0.3 })
        );
        lens.rotation.x = Math.PI / 2;
        lens.position.z = 0.26;
        cameraGroup.add(lens);
        scene.add(cameraGroup);

        // Controls
        const azimuthRing = new THREE.Mesh(
            new THREE.TorusGeometry(AZIMUTH_RADIUS, 0.04, 16, 64),
            new THREE.MeshStandardMaterial({ color: 0x00ff88, emissive: 0x00ff88, emissiveIntensity: 0.3 })
        );
        azimuthRing.rotation.x = Math.PI / 2;
        azimuthRing.position.copy(CENTER);
        scene.add(azimuthRing);

        const azimuthHandle = new THREE.Mesh(
            new THREE.SphereGeometry(0.18, 16, 16),
            new THREE.MeshStandardMaterial({ color: 0x00ff88, emissive: 0x00ff88, emissiveIntensity: 0.5 })
        );
        azimuthHandle.userData.type = 'azimuth';
        scene.add(azimuthHandle);

        // Elevation arc (limited: pitch -30..60) and full arc (0..360) so UI can switch with "continuous"
        const arcPointsLimited = [];
        for (let i = 0; i <= 32; i++) {
            const angle = THREE.MathUtils.degToRad(-30 + (90 * i / 32));
            arcPointsLimited.push(
                new THREE.Vector3(
                    CENTER.x,
                    CENTER.y + ELEVATION_RADIUS * Math.sin(angle),
                    CENTER.z + ELEVATION_RADIUS * Math.cos(angle)
                )
            );
        }
        const arcCurveLimited = new THREE.CatmullRomCurve3(arcPointsLimited);
        const elevationArcLimited = new THREE.Mesh(
            new THREE.TubeGeometry(arcCurveLimited, 32, 0.04, 8, false),
            new THREE.MeshStandardMaterial({ color: 0xff69b4, emissive: 0xff69b4, emissiveIntensity: 0.3 })
        );
        elevationArcLimited.visible = !Boolean((valueRef.current as any)?.continuous);
        scene.add(elevationArcLimited);

        const arcPointsFull = [];
        for (let i = 0; i <= 64; i++) {
            const angle = (Math.PI * 2 * i) / 64;
            arcPointsFull.push(
                new THREE.Vector3(
                    CENTER.x,
                    CENTER.y + ELEVATION_RADIUS * Math.sin(angle),
                    CENTER.z + ELEVATION_RADIUS * Math.cos(angle)
                )
            );
        }
        const arcCurveFull = new THREE.CatmullRomCurve3(arcPointsFull);
        const elevationArcFull = new THREE.Mesh(
            new THREE.TubeGeometry(arcCurveFull, 64, 0.04, 8, false),
            new THREE.MeshStandardMaterial({ color: 0xff69b4, emissive: 0xff69b4, emissiveIntensity: 0.2 })
        );
        elevationArcFull.visible = Boolean((valueRef.current as any)?.continuous);
        scene.add(elevationArcFull);

        const elevationHandle = new THREE.Mesh(
            new THREE.SphereGeometry(0.18, 16, 16),
            new THREE.MeshStandardMaterial({ color: 0xff69b4, emissive: 0xff69b4, emissiveIntensity: 0.5 })
        );
        elevationHandle.userData.type = 'elevation';
        scene.add(elevationHandle);

        const distanceLineGeo = new THREE.BufferGeometry();
        const distanceLine = new THREE.Line(distanceLineGeo, new THREE.LineBasicMaterial({ color: 0xffa500 }));
        scene.add(distanceLine);

        const distanceHandle = new THREE.Mesh(
            new THREE.SphereGeometry(0.18, 16, 16),
            new THREE.MeshStandardMaterial({ color: 0xffa500, emissive: 0xffa500, emissiveIntensity: 0.5 })
        );
        distanceHandle.userData.type = 'distance';
        scene.add(distanceHandle);

        sceneObjects.current = {
            cameraGroup, azimuthHandle, elevationHandle, distanceHandle, distanceLineGeo, targetPlane,
            planeMaterial, scene, camera, renderer, azimuthRing,
            elevationArcLimited, elevationArcFull
        };
        
        // Initialize orbit view state from the actual camera position
        try {
            const v = camera.position.clone().sub(CENTER);
            const r = v.length();
            const az = THREE.MathUtils.radToDeg(Math.atan2(v.x, v.z));
            const el = THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(v.y / (r || 1), -1, 1)));
            state.current.viewRadius = r;
            state.current.viewAzimuth = (az + 360) % 360;
            state.current.viewElevation = THREE.MathUtils.clamp(el, -80, 80);
        } catch {}

        updatePositions();
        updateViewCamera();

        const raycaster = new THREE.Raycaster();
        const mouse = new THREE.Vector2();
        
        const getMousePos = (e: MouseEvent | TouchEvent) => {
            const rect = renderer.domElement.getBoundingClientRect();
            const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
            const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
            return {
                x: ((clientX - rect.left) / rect.width) * 2 - 1,
                y: -((clientY - rect.top) / rect.height) * 2 + 1
            };
        };

        const handleStart = (e: MouseEvent | TouchEvent) => {
            const m = getMousePos(e);
            mouse.set(m.x, m.y);
            raycaster.setFromCamera(mouse, camera);
            
            const intersects = raycaster.intersectObjects([azimuthHandle, elevationHandle, distanceHandle]);
            if (intersects.length > 0) {
                state.current.isDragging = true;
                state.current.dragTarget = intersects[0].object;
                state.current.dragStartMouse.set(mouse.x, mouse.y);
                state.current.dragStartDistance = state.current.distance;
                
                (intersects[0].object as THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>).material.emissiveIntensity = 1.0;
                intersects[0].object.scale.setScalar(1.3);
                
                renderer.domElement.style.cursor = 'grabbing';
                
                e.preventDefault();
                return;
            }

            // Start orbiting the view when dragging on empty space
            state.current.isOrbiting = true;
            state.current.orbitStartMouse.set(mouse.x, mouse.y);
            state.current.orbitStartViewAzimuth = state.current.viewAzimuth;
            state.current.orbitStartViewElevation = state.current.viewElevation;
            renderer.domElement.style.cursor = 'grabbing';
            e.preventDefault();
        };

        const handleMove = (e: MouseEvent | TouchEvent) => {
            const m = getMousePos(e);
            mouse.set(m.x, m.y);
            raycaster.setFromCamera(mouse, camera);

            if (state.current.isDragging && state.current.dragTarget) {
                const target = state.current.dragTarget;
                const intersection = new THREE.Vector3();

                if (target.userData.type === 'azimuth') {
                    // Horizontal plane through orbit center
                    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -CENTER.y);
                    if (raycaster.ray.intersectPlane(plane, intersection)) {
                        let az = THREE.MathUtils.radToDeg(
                            Math.atan2(intersection.x - CENTER.x, intersection.z - CENTER.z)
                        );
                        if (az < 0) az += 360;
                        state.current.azimuth = az;
                    }
                } else if (target.userData.type === 'elevation') {
                    // Vertical plane through orbit center (YZ plane)
                    const plane = new THREE.Plane(new THREE.Vector3(1, 0, 0), -CENTER.x);
                    if (raycaster.ray.intersectPlane(plane, intersection)) {
                        const relY = intersection.y - CENTER.y;
                        const relZ = intersection.z - CENTER.z;
                        let pitch = THREE.MathUtils.radToDeg(Math.atan2(relY, relZ)); // pitch in degrees, 0=eye level
                        if (Boolean((valueRef.current as any)?.continuous)) {
                            // Store as "仰角" (0..360) where 90=eye level => pitch + 90
                            state.current.elevation = ((pitch + 90) % 360 + 360) % 360;
                        } else {
                            pitch = THREE.MathUtils.clamp(pitch, -30, 60);
                            state.current.elevation = pitch + 90;
                        }
                    }
                } else if (target.userData.type === 'distance') {
                    const deltaY = mouse.y - state.current.dragStartMouse.y;
                    const maxDist = Boolean((valueRef.current as any)?.continuous) ? 4 : 1.4;
                    state.current.distance = THREE.MathUtils.clamp(
                        state.current.dragStartDistance + deltaY * 1.5,
                        0.6,
                        maxDist
                    );
                }
                updatePositions();
            } else if (state.current.isOrbiting) {
                // Orbit view camera around CENTER
                const dx = mouse.x - state.current.orbitStartMouse.x;
                const dy = mouse.y - state.current.orbitStartMouse.y;
                // Scale deltas from NDC (-1..1) to degrees; tuned for comfortable feel
                const az = state.current.orbitStartViewAzimuth + dx * 180;
                const el = state.current.orbitStartViewElevation - dy * 120;
                state.current.viewAzimuth = (az + 360) % 360;
                state.current.viewElevation = THREE.MathUtils.clamp(el, -80, 80);
                updateViewCamera();
                e.preventDefault();
            } else {
                const intersects = raycaster.intersectObjects([azimuthHandle, elevationHandle, distanceHandle]);
                [azimuthHandle, elevationHandle, distanceHandle].forEach(h => {
                     (h.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.5;
                     h.scale.setScalar(1);
                });
                
                if (intersects.length > 0) {
                     (intersects[0].object as THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>).material.emissiveIntensity = 0.8;
                     intersects[0].object.scale.setScalar(1.1);
                     renderer.domElement.style.cursor = 'grab';
                } else {
                     renderer.domElement.style.cursor = 'default';
                }
            }
        };

        const handleEnd = () => {
             if (state.current.isDragging) {
                const { azimuth, elevation, distance } = state.current;
                const isCont = Boolean((valueRef.current as any)?.continuous);
                if (isCont) {
                    // Keep continuous values (no snapping). Normalize angles to 0..360.
                    let az = azimuth % 360;
                    if (az < 0) az += 360;
                    let el = elevation % 360;
                    if (el < 0) el += 360;
                    const dist = THREE.MathUtils.clamp(distance, 0.6, 4);
                    state.current.azimuth = az;
                    state.current.elevation = el;
                    state.current.distance = dist;
                    updatePositions();
                    onChangeRef.current({ azimuth: az, elevation: el, distance: dist });
                } else {
                    const snappedAz = snapToNearest(azimuth, AZIMUTH_STEPS);
                    const snappedEl = snapToNearest(elevation, ELEVATION_STEPS);
                    const snappedDist = snapToNearest(distance, DISTANCE_STEPS);

                    state.current.azimuth = snappedAz;
                    state.current.elevation = snappedEl;
                    state.current.distance = snappedDist;
                    updatePositions();
                    onChangeRef.current({ azimuth: snappedAz, elevation: snappedEl, distance: snappedDist });
                }

                if (state.current.dragTarget) {
                    (state.current.dragTarget as THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>).material.emissiveIntensity = 0.5;
                    state.current.dragTarget.scale.setScalar(1);
                }
             }

             // End orbiting
             state.current.isOrbiting = false;
             
             state.current.isDragging = false;
             state.current.dragTarget = null;
             renderer.domElement.style.cursor = 'default';
        };

        const canvas = renderer.domElement;
        canvas.addEventListener('mousedown', handleStart);
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleEnd);
        canvas.addEventListener('touchstart', handleStart, { passive: false });
        window.addEventListener('touchmove', handleMove, { passive: false });
        window.addEventListener('touchend', handleEnd);

        let frameId: number;
        const animate = () => {
            frameId = requestAnimationFrame(animate);
            // Keep view camera in sync with orbit state
            updateViewCamera();
            renderer.render(scene, camera);
        };
        animate();

        const resizeObs = new ResizeObserver(() => {
             const w = container.clientWidth;
             const h = container.clientHeight;
             camera.aspect = w / h;
             camera.updateProjectionMatrix();
             renderer.setSize(w, h);
        });
        resizeObs.observe(container);

        return () => {
            cancelAnimationFrame(frameId);
            resizeObs.disconnect();
            canvas.removeEventListener('mousedown', handleStart);
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleEnd);
            canvas.removeEventListener('touchstart', handleStart);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', handleEnd);
            
            if (renderer) {
                renderer.dispose();
                container.removeChild(renderer.domElement);
            }
        };
    }, []);

    return (
        <div 
            ref={containerRef} 
            className={`${className || ''} nodrag`}
            style={{ position: 'relative', width: '100%', height: '100%', minHeight: 0 }}
        >
            <div 
                ref={mountRef} 
                style={{ width: '100%', height: '100%', borderRadius: '8px', overflow: 'hidden' }} 
            />
        </div>
    );
};

